import React, { useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { twMerge } from "tailwind-merge";

const digitVariants = {
  initial: (direction: number) => ({
    y: direction > 0 ? -10 : 10,
    opacity: 0,
  }),
  animate: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.2 },
  },
  exit: (direction: number) => ({
    y: direction > 0 ? 10 : -10,
    opacity: 0,
    transition: { duration: 0.2 },
  }),
};

function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>(undefined);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

const DigitSlot: React.FC<{ digit: string; prevDigit: string }> = ({
  digit,
  prevDigit,
}) => {
  const direction = Number(digit) >= Number(prevDigit) ? 1 : -1;

  return (
    <AnimatePresence mode="popLayout">
      <motion.span
        key={digit}
        custom={direction}
        variants={digitVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="inline-block min-w-1.5"
      >
        {digit}
      </motion.span>
    </AnimatePresence>
  );
};

const AnimatedNumber: React.FC<{ number: number; className?: string }> = ({
  number,
  className,
}) => {
  const prevNumber = usePrevious(number);
  const currentDigits = String(number).split("");
  const previousDigits =
    prevNumber !== undefined ? String(prevNumber).split("") : currentDigits;

  return (
    <p className={twMerge(className)}>
      {currentDigits.map((digit, index) => (
        <DigitSlot
          digit={digit}
          prevDigit={previousDigits[index] || digit}
          key={index}
        />
      ))}
    </p>
  );
};

export default AnimatedNumber;
